// --- Dependencies -------------------------------------------------------------------------------
import { useState, type ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyTab = { value: string; label: string; count?: number };

type Props = {
    /** The record being looked at. The section name is the AppBar's job, so this is usually a name. */
    title?: string;
    onBack?: () => void;

    /**
     * The one or two divisions that matter most. Capped at three on purpose — see `MAX_TABS`; a longer
     * enum belongs in the filter panel, where it is a labelled dropdown rather than a row of guesses.
     */
    tabs?: TyTab[];
    tab?: string;
    onTab?: (value: string) => void;

    search?: { value: string; onChange: (value: string) => void; placeholder: string };
    /** The filter inputs themselves — the bar only owns showing and hiding them. */
    filters?: ReactNode;
    activeFilterCount?: number;
    onClearFilters?: () => void;

    addLabel?: string;
    onAdd?: () => void;
    addDisabled?: boolean;

    /** Screen-specific controls, dropped in beside the standard ones. */
    children?: ReactNode;
};

// --- Constants ----------------------------------------------------------------------------------

/**
 * Three is the point where a row of tabs stops being scannable and starts being a menu wearing the
 * wrong clothes — past it nothing is visibly "the current one" at a glance, which is the only thing
 * tabs are better at than a dropdown.
 */
const MAX_TABS = 3;

// --- Logic --------------------------------------------------------------------------------------

/**
 * The bar above every list. In its resting state it carries the tabs and the create button; opening the
 * filters replaces that entirely with a search box and the screen's own filter inputs.
 *
 * Replacing rather than expanding is deliberate: filtering is a mode, and leaving the create button
 * sitting beside a half-built query invites the one misclick that matters on a dashboard with delete
 * powers. The filter count stays visible on the toggle while the panel is shut, so a narrowed list is
 * never silently narrowed.
 *
 * The bar is one row at every width and never wraps. Its contents scroll sideways inside an inner
 * track while the controls that close or clear the mode stay pinned outside it — a bar that wrapped
 * would grow downward and shove the table with it every time a filter was added, which is exactly the
 * moment the rows need to stay where they were.
 */
export default function ActionsBar({
    title,
    onBack,
    tabs,
    tab,
    onTab,
    search,
    filters,
    activeFilterCount = 0,
    onClearFilters,
    addLabel,
    onAdd,
    addDisabled,
    children,
}: Props) {
    const t = useTranslation();
    const [isFiltering, setIsFiltering] = useState(false);

    const hasFilterPanel = Boolean(search || filters);

    if (isFiltering && hasFilterPanel)
        return (
            <div className="actions-bar actions-bar--filtering">
                {/* Everything that can overflow lives in here and scrolls; the buttons below never do. */}
                <div className="actions-bar-track">
                    {search && (
                        <div className="actions-bar-search">
                            <img className="actions-bar-search-icon" src="/icons/search-icon.svg" alt="" />
                            <input
                                className="actions-bar-search-input"
                                type="search"
                                value={search.value}
                                placeholder={search.placeholder}
                                onChange={(e) => search.onChange(e.target.value)}
                            />
                        </div>
                    )}

                    <div className="actions-bar-filters">{filters}</div>
                </div>

                <div className="actions-bar-pinned">
                    {activeFilterCount > 0 && onClearFilters && (
                        <button type="button" className="actions-bar-icon-btn" onClick={onClearFilters} aria-label={t.table.filtersClear}>
                            <img src="/icons/clear-icon.svg" alt="" width={15} height={15} />
                        </button>
                    )}
                    <button type="button" className="actions-bar-icon-btn" onClick={() => setIsFiltering(false)} aria-label={t.table.filtersClose}>
                        <img src="/icons/close-icon.svg" alt="" width={13} height={13} />
                    </button>
                </div>
            </div>
        );

    return (
        <div className="actions-bar">
            {onBack && (
                <button type="button" className="actions-bar-back-btn" onClick={onBack} aria-label={t.table.back}>
                    <img src="/icons/arrow-dark-icon.svg" alt="" width={20} height={20} />
                </button>
            )}

            {title && <p className="actions-bar-title">{title}</p>}

            <div className="actions-bar-track">
                {tabs && tabs.length > 0 && (
                    <div className="actions-bar-tabs" role="tablist">
                        {tabs.slice(0, MAX_TABS).map((one) => (
                            <button
                                key={one.value}
                                type="button"
                                role="tab"
                                className="actions-bar-tab"
                                data-active={one.value === tab}
                                aria-selected={one.value === tab}
                                onClick={() => onTab?.(one.value)}
                            >
                                {one.label}
                                {one.count !== undefined && <span className="actions-bar-tab-count">{one.count}</span>}
                            </button>
                        ))}
                    </div>
                )}

                {children}
            </div>

            <div className="actions-bar-pinned">
                {hasFilterPanel && (
                    <button type="button" className="actions-bar-icon-btn" onClick={() => setIsFiltering(true)} aria-label={t.table.filtersOpen}>
                        <img src="/icons/filter-icon.svg" alt="" width={16} height={16} />
                        {activeFilterCount > 0 && <span className="actions-bar-badge">{activeFilterCount}</span>}
                    </button>
                )}

                {onAdd && (
                    <button type="button" className="actions-bar-add-btn" onClick={onAdd} disabled={addDisabled}>
                        <img src="/icons/plus-icon.svg" alt="" width={16} height={16} />
                        <span className="actions-bar-add-label">{addLabel}</span>
                    </button>
                )}
            </div>
        </div>
    );
}
